import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Users } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field, usePagination, Pagination, useDesplazarAlAbrir } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  rnc: string;
  contacto: string;
  telefono: string;
  email: string;
  direccion: string;
  ubicacion: string;
  tipo: 'Industria' | 'Comercio' | 'Residencial' | 'Otro';
  capacidadTanque: number;
  estado: 'activo' | 'inactivo';
  observaciones: string;
  fecha_registro: string;
  ultima_compra?: string;
  dias_sin_comprar?: number;
  representante?: string;
  representanteCedula?: string;
  representanteDireccion?: string;
}
interface ClientesProps {
  clientes: Cliente[];
  onAddCliente: (cliente: Cliente) => void;
  onDeleteCliente: (id: string) => void;
  onUpdateCliente: (cliente: Cliente) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Clientes({
  clientes,
  onAddCliente,
  onDeleteCliente,
  onUpdateCliente
}: ClientesProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Al editar un registro del final del listado, el formulario se abre arriba:
  // sin llevar la vista hasta él parece que el botón no hizo nada.
  const refForm = useDesplazarAlAbrir(showForm, editingId);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'activo' | 'inactivo'>('todos');
  const [formData, setFormData] = useState<Partial<Cliente>>({
    tipo: 'Comercio',
    estado: 'activo',
    capacidadTanque: 1000
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.codigo || !formData.nombre || !formData.email) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    if (editingId) {
      onUpdateCliente({
        ...formData,
        id: editingId
      } as Cliente);
      setEditingId(null);
    } else {
      onAddCliente({
        ...formData,
        id: Date.now().toString(),
        fecha_registro: new Date().toISOString().split('T')[0]
      } as Cliente);
    }
    setFormData({
      tipo: 'Comercio',
      estado: 'activo',
      capacidadTanque: 1000
    });
    setShowForm(false);
  };
  const handleEdit = (cliente: Cliente) => {
    setFormData(cliente);
    setEditingId(cliente.id);
    setShowForm(true);
  };
  const filteredClientes = clientes.filter(c => {
    const matchSearch = c.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  // Paginado: un listado largo obliga a hacer scroll para llegar a lo
  // último y hace la página pesada de dibujar.
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredClientes);
  const clientesActivos = clientes.filter(c => c.estado === 'activo').length;
  const clientesInactivos = clientes.filter(c => c.estado === 'inactivo').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-188" data-mp="30i6at7rxvkc3">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-189" data-mp="2qqad3xtrlw8e">
        
        <StatCard label="Total clientes" value={String(clientes.length)} note="Registrados en el sistema" accent={T.info} data-id="element-190" data-mp="3efcsez0xfczq" />
        
        <StatCard label="Activos" value={String(clientesActivos)} note="Comprando actualmente" accent={T.ok} data-id="element-191" data-mp="3j8xtx11qap3o" />
        
        <StatCard label="Inactivos" value={String(clientesInactivos)} note="Sin actividad reciente" accent={T.danger} data-id="element-192" data-mp="1qjgf45h89kzy" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-193" data-mp="1lkltywvsfkco">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-194" data-mp="1gmamutwvnny5">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-195" data-mp="2eb3ubq54n7me">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-196" data-mp="29c11r56c2tvb" />
            
            <input type="text" placeholder="Buscar por código, nombre o email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-197" data-mp="2wzinaiz08vpu" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-198" data-mp="2zlhxw5y8f6ee">
            
            <option value="todos" data-id="element-199" data-mp="39huyzow3wmj6">Todos los estados</option>
            <option value="activo" data-id="element-200" data-mp="3eh7o6gaga5ox">Activos</option>
            <option value="inactivo" data-id="element-201" data-mp="3ehz98x6f8l0y">Inactivos</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            tipo: 'Comercio',
            estado: 'activo',
            capacidadTanque: 1000
          });
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-202" data-mp="3ijcg9al92u9g">
            
            <Plus size={15} data-id="element-203" data-mp="61nzzvomzdgq" /> Nuevo Cliente
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-204" data-mp="a2hjmcv1q63t">
        {showForm && <motion.div ref={refForm} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-205" data-mp="a3ivuknssyn4">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-206" data-mp="2fy1tss1a1ezs">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-207" data-mp="2b3nx8mavp1tj">
              
                {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-208" data-mp="2yujif0ktwyrt">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-209" data-mp="2yu9ste3p4tmn">
                
                  <Field label="Código *" data-id="element-210" data-mp="14g8wp0zj6kq9">
                    <input type="text" value={formData.codigo || ''} onChange={e => setFormData({
                  ...formData,
                  codigo: e.target.value
                })} style={inputStyle} placeholder="CLI-001" data-id="element-211" data-mp="14h25e7i1vkb1" />
                  
                  </Field>
                  <Field label="Nombre de empresa *" data-id="element-212" data-mp="ubk1m92ghm83">
                    <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
                  ...formData,
                  nombre: e.target.value
                })} style={inputStyle} placeholder="Nombre empresa" data-id="element-213" data-mp="glgktxmcikq1" />
                  
                  </Field>
                  <Field label="RNC" data-id="element-214" data-mp="6plz2tmts9j1">
                    <input type="text" value={formData.rnc || ''} onChange={e => setFormData({
                  ...formData,
                  rnc: e.target.value
                })} style={inputStyle} placeholder="123456789" data-id="element-215" data-mp="bmm3z17xv68k" />
                  
                  </Field>
                  <Field label="Tipo de cliente" data-id="element-216" data-mp="1s0lzlzzr49q0">
                    <select value={formData.tipo || 'Comercio'} onChange={e => setFormData({
                  ...formData,
                  tipo: e.target.value as any
                })} style={inputStyle} data-id="element-217" data-mp="1s1x27whgnbfj">
                    
                      <option data-id="element-218" data-mp="35lysbmi5c25b">Industria</option>
                      <option data-id="element-219" data-mp="30m2n5jxpq4o6">Comercio</option>
                      <option data-id="element-220" data-mp="30n48ch8g07jm">Residencial</option>
                      <option data-id="element-221" data-mp="2vtjdgy25bton">Otro</option>
                    </select>
                  </Field>
                  <Field label="Persona de contacto" data-id="element-222" data-mp="2rtivx4jr32dd">
                    <input type="text" value={formData.contacto || ''} onChange={e => setFormData({
                  ...formData,
                  contacto: e.target.value
                })} style={inputStyle} placeholder="Nombre contacto" data-id="element-223" data-mp="2rqwp574ooqw7" />
                  
                  </Field>
                  <Field label="Propietario / representante legal" data-id="element-224" data-mp="2cxjfn6mhlvx5">
                    <input type="text" value={formData.representante || ''} onChange={e => setFormData({
                  ...formData,
                  representante: e.target.value
                })} style={inputStyle} placeholder="Nombre completo (para el contrato)" data-id="element-225" data-mp="28v6llfh3rmg7" />
                  
                  </Field>
                  <Field label="Cédula del representante" data-id="element-226" data-mp="1ozv7skh0lhb4">
                    <input type="text" value={formData.representanteCedula || ''} onChange={e => setFormData({
                  ...formData,
                  representanteCedula: e.target.value
                })} style={inputStyle} placeholder="000-0000000-0" data-id="element-227" data-mp="1tyewgsmzt776" />
                  
                  </Field>
                  <Field label="Teléfono" data-id="element-228" data-mp="162avecvfsb7e">
                    <input type="tel" value={formData.telefono || ''} onChange={e => setFormData({
                  ...formData,
                  telefono: e.target.value
                })} style={inputStyle} placeholder="+1-809-..." data-id="element-229" data-mp="163c96p3u7bst" />
                  
                  </Field>
                  <Field label="Email *" data-id="element-230" data-mp="1k2bvhq3vnelt">
                    <input type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} style={inputStyle} placeholder="empresa@email.com" data-id="element-231" data-mp="2qhcrfvswjwse" />
                  
                  </Field>
                  <Field label="Ubicación" data-id="element-232" data-mp="3ga57ooxprhi5">
                    <input type="text" value={formData.ubicacion || ''} onChange={e => setFormData({
                  ...formData,
                  ubicacion: e.target.value
                })} style={inputStyle} placeholder="Zona/Región" data-id="element-233" data-mp="3g93lppf5taln" />
                  
                  </Field>
                  <Field label="Capacidad tanque (galones)" data-id="element-234" data-mp="2sczjv7fs4akt">
                    <input type="number" value={formData.capacidadTanque || 1000} onChange={e => setFormData({
                  ...formData,
                  capacidadTanque: parseInt(e.target.value)
                })} style={inputStyle} placeholder="1000" data-id="element-235" data-mp="2seiv2jmkvvfq" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-236" data-mp="hk50f4g1y926">
                    <select value={formData.estado || 'activo'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-237" data-mp="hj3geah26c8b">
                    
                      <option value="activo" data-id="element-238" data-mp="265vcpjeerhbt">Activo</option>
                      <option value="inactivo" data-id="element-239" data-mp="2238jpf603qd1">Inactivo</option>
                    </select>
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-240" data-mp="1x9w57xv1czy0">
                    <Field label="Dirección" data-id="element-241" data-mp="1sbkvro8nkrgb">
                      <input type="text" value={formData.direccion || ''} onChange={e => setFormData({
                    ...formData,
                    direccion: e.target.value
                  })} style={inputStyle} placeholder="Dirección completa" data-id="element-242" data-mp="1nd9mpfq6mkz5" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-243" data-mp="19e9y282o662z">
                    <Field label="Domicilio personal del representante (para el contrato)" data-id="element-244" data-mp="38qchjiban0gn">
                      <input type="text" value={formData.representanteDireccion || ''} onChange={e => setFormData({
                    ...formData,
                    representanteDireccion: e.target.value
                  })} style={inputStyle} placeholder="Calle, sector, ciudad" data-id="element-245" data-mp="33rsqx4lr90hy" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-246" data-mp="3dxaub1xfswkd">
                    <Field label="Observaciones" data-id="element-247" data-mp="3iv2jvpam06le">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-248" data-mp="af0q3hg8tnsm" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-249" data-mp="2l60sih7tkhcg">
                  <button type="submit" style={primaryButton} data-id="element-250" data-mp="2q3spvqr8gvo1">
                    {editingId ? 'Actualizar' : 'Guardar'} cliente
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-251" data-mp="2dm85hjgp4gf6">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Clientes */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-252" data-mp="1uxbj01rjis7i">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-253" data-mp="1uup4fi68fcfs">
          
          Clientes ({filteredClientes.length})
        </h3>
        {filteredClientes.length === 0 ? <EmptyState icon={<Users size={20} data-id="element-255" data-mp="1g73yzhlvx14p" />} title="No hay clientes" hint="Registra un cliente nuevo o ajusta los filtros de búsqueda." data-id="element-254" data-mp="1g7m1ux31ydll" /> : <div style={{
        overflowX: 'auto'
      }} data-id="element-256" data-mp="1b816sxr06pe5">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-257" data-mp="1ovrrwoqwrjmf">
              <thead data-id="element-258" data-mp="1tvnucjjyj2zv">
                <tr data-id="element-259" data-mp="1tv4fxctimwql">
                  {['Código', 'Empresa', 'Tipo', 'Contacto', 'Teléfono', 'Tanque (gal)', 'Estado', 'Acciones'].map((h, i) => <th key={h} style={{
                textAlign: i === 7 ? 'center' : 'left',
                padding: '0 10px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-260" data-mp="1yruutwtl3maf" data-mpi={i}>
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-261" data-mp="n65v7pte5kg9">
                {pageItems.map(cliente => <tr key={cliente.id} data-id="element-262" data-mp="n7h8qhcn26kk">
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontFamily: 'monospace',
                color: T.info,
                fontWeight: 700
              }} data-id="element-263" data-mp="n8imwuoyb96i">
                  
                      {cliente.codigo}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontWeight: 600,
                color: T.text
              }} data-id="element-264" data-mp="2wmp1kddwww78">
                  
                      {cliente.nombre}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-265" data-mp="3kd2mljy2xusb">
                  
                      <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 9px',
                  borderRadius: 99,
                  background: T.surfaceSubtle,
                  color: T.textSecondary
                }} data-id="element-266" data-mp="3ffsyq8qc9r6l">
                    
                        {cliente.tipo}
                      </span>
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-267" data-mp="20eiy2te3iq9o">
                  
                      {cliente.contacto}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-268" data-mp="1wdpbc3ce3hid">
                  
                      {cliente.telefono}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.text
              }} data-id="element-269" data-mp="18pzdayriccd6">
                  
                      {cliente.capacidadTanque}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-270" data-mp="ytcro7rcxha3">
                  
                      <Badge label={cliente.estado} color={cliente.estado === 'activo' ? T.ok : T.danger} soft={cliente.estado === 'activo' ? T.okSoft : T.dangerSoft} data-id="element-271" data-mp="k5cgljxf4s21" />
                  
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                textAlign: 'center'
              }} data-id="element-272" data-mp="u4bq14q0crq3">
                  
                      <div style={{
                  display: 'inline-flex',
                  gap: 6,
                  justifyContent: 'center'
                }} data-id="element-273" data-mp="131uhory5q4ay">
                    
                        <button onClick={() => handleEdit(cliente)} title="Editar" style={{
                    ...iconBtn,
                    color: T.info
                  }} data-id="element-274" data-mp="1crx8qmlpx86n">
                      
                          <Edit2 size={14} data-id="element-275" data-mp="1lug82wqap2o5" />
                        </button>
                        <button onClick={() => onDeleteCliente(cliente.id)} title="Eliminar" style={{
                    ...iconBtn,
                    color: T.danger
                  }} data-id="element-276" data-mp="1ht4i2q3iefff">
                      
                          <Trash2 size={14} data-id="element-277" data-mp="2t52lzk6p6dyh" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
        {filteredClientes.length ? <div style={{
        marginTop: 16
      }} data-id="element-278" data-mp="324y0nyzgor39">
            <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="clientes" data-id="element-279" data-mp="25huhh4h3f8xi" />

          </div> : null}
      </div>
    </div>;
}